import * as React from 'react';
import { useTranslation } from 'react-i18next';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import Button from '@mui/material/Button';
import IconButton from '@mui/material/IconButton';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import Autocomplete from '@mui/material/Autocomplete';
import TextField from '@mui/material/TextField';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogActions from '@mui/material/DialogActions';
import Input from '@mui/material/Input';
import Divider from '@mui/material/Divider';
import CircularProgress from '@mui/material/CircularProgress';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import EmailIcon from '@mui/icons-material/Email';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import dayjs, { Dayjs } from 'dayjs';
import { Survey, TableSurveysRow } from './TableSurveysRow';
import { Team } from './TableTeamsRow';
import { CohortTeamSection } from './CohortTeamSection';
import { Cohort } from './TableCohortsRow';
import { CohortEmailDialog, CohortDateEmailConfig } from './CohortEmailDialog';
import { COHORT_DATE_COUNT } from '../Constants';

function cohortDatesToDayjs(cohort: Cohort): (Dayjs | null)[] {
  return Array.from({ length: COHORT_DATE_COUNT }, (_, i) => {
    const val = cohort[`date${i + 1}` as keyof Cohort] as string | undefined;
    if (!val) return null;
    const d = dayjs(val, 'YYYY-MM-DD', true);
    return d.isValid() ? d : null;
  });
}

function dayjsToDateStrings(dates: (Dayjs | null)[]): string[] {
  return dates.map((d) => (d && d.isValid() ? d.format('YYYY-MM-DD') : ''));
}

interface CohortDetailPanelProps {
  cohort: Cohort;
  teams: Team[];
  uncohortedTeams: Team[];
  allSurveys: Survey[];
  assignedSurveyIds: Set<string>;
  onEdit: (
    cohort: Cohort,
    name: string,
    systemLeader: string,
    dates: string[],
    dateNames: string[]
  ) => Promise<void>;
  onDelete: (cohortId: string) => Promise<void>;
  onAddSurveytoCohort: (cohortId: string, surveyIds: string[]) => Promise<void>;
  onRemoveSurveyFromCohort: (cohortId: string, surveyId: string) => Promise<void>;
  onAssignTeam: (teamId: string) => Promise<void>;
  onRemoveTeamFromCohort: (teamId: string) => Promise<void>;
  onEditTeam: (team: Team, name: string, leader: string) => Promise<void>;
  onDeleteTeam: (teamId: string, deleteSurveys: boolean) => Promise<void>;
  onAddSurveyToTeam: (teamId: string, surveyIds: string[]) => Promise<void>;
  onRemoveSurveyFromTeam: (teamId: string, surveyId: string) => Promise<void>;
  onEditEmailConfig: (
    cohortId: string,
    dateIndex: string,
    config: CohortDateEmailConfig
  ) => Promise<void>;
  onUploadAttachment: (
    cohortId: string,
    fileName: string,
    fileType: string
  ) => Promise<{ uploadUrl: string; fileKey: string }>;
  onSendEmails: (
    cohortId: string,
    dateIndex: string
  ) => Promise<{ sent: number; errors: string[] }>;
  analyzeSurvey: (surveyId: string | number) => void;
  loading: string | number;
  getParticipants: (surveyId: string | number) => void;
  participantsBySurvey: { [key: string]: any };
  setQrModalOpen: (link: string) => void;
  loadingParticipants: string | number;
  isMobile: boolean;
  editLeaderReportDate: (
    survey: Survey,
    leader: string,
    reportDate: Dayjs | null,
    reminderDate: Dayjs | null
  ) => void;
  getLeaderAndReportDate: (surveyId: string | number) => void;
  loadingLeader: string | number;
  leaderBySurvey: { [key: string]: any };
  onBack?: () => void;
}

export const CohortDetailPanel: React.FC<CohortDetailPanelProps> = ({
  cohort,
  teams,
  uncohortedTeams,
  allSurveys,
  assignedSurveyIds,
  onEdit,
  onDelete,
  onAddSurveytoCohort,
  onRemoveSurveyFromCohort,
  onAssignTeam,
  onRemoveTeamFromCohort,
  onEditTeam,
  onDeleteTeam,
  onAddSurveyToTeam,
  onRemoveSurveyFromTeam,
  onEditEmailConfig,
  onUploadAttachment,
  onSendEmails,
  analyzeSurvey,
  loading,
  getParticipants,
  participantsBySurvey,
  setQrModalOpen,
  loadingParticipants,
  isMobile,
  editLeaderReportDate,
  getLeaderAndReportDate,
  loadingLeader,
  leaderBySurvey,
  onBack,
}) => {
  const { t } = useTranslation();
  const [editDialogOpen, setEditDialogOpen] = React.useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = React.useState(false);
  const [emailDialogOpen, setEmailDialogOpen] = React.useState(false);
  const [editName, setEditName] = React.useState(cohort.name);
  const [editSystemLeader, setEditSystemLeader] = React.useState(cohort.systemLeader);
  const [editDates, setEditDates] = React.useState<(Dayjs | null)[]>(cohortDatesToDayjs(cohort));
  const [editDateNames, setEditDateNames] = React.useState<string[]>(
    Array.from(
      { length: COHORT_DATE_COUNT },
      (_, i) => cohort.dateNames?.[i] ?? t(`cohorts.date_default_name_${i + 1}`)
    )
  );
  const [editLoading, setEditLoading] = React.useState(false);
  const [deleteLoading, setDeleteLoading] = React.useState(false);
  const [addSurveyLoading, setAddSurveyLoading] = React.useState(false);
  const [removingId, setRemovingId] = React.useState<string | null>(null);
  const [selectedSurveys, setSelectedSurveys] = React.useState<Survey[]>([]);
  const [selectedTeam, setSelectedTeam] = React.useState<Team | null>(null);
  const [assignTeamLoading, setAssignTeamLoading] = React.useState(false);

  const cohortSurveys = allSurveys.filter((s) => cohort.surveyIds.includes(String(s.i)));
  const availableSurveys = allSurveys
    .filter((s) => !assignedSurveyIds.has(String(s.i)))
    .sort((a, b) => a.t.localeCompare(b.t));

  const openEditDialog = () => {
    setEditName(cohort.name);
    setEditSystemLeader(cohort.systemLeader);
    setEditDates(cohortDatesToDayjs(cohort));
    setEditDateNames(
      Array.from(
        { length: COHORT_DATE_COUNT },
        (_, i) => cohort.dateNames?.[i] ?? t(`cohorts.date_default_name_${i + 1}`)
      )
    );
    setSelectedSurveys([]);
    setEditDialogOpen(true);
  };

  const handleEdit = async () => {
    setEditLoading(true);
    await onEdit(cohort, editName, editSystemLeader, dayjsToDateStrings(editDates), editDateNames);
    setEditLoading(false);
    setEditDialogOpen(false);
  };

  const handleDelete = async () => {
    setDeleteLoading(true);
    await onDelete(cohort.id);
    setDeleteLoading(false);
    setDeleteDialogOpen(false);
  };

  const handleAddSurvey = async () => {
    if (selectedSurveys.length === 0) return;
    setAddSurveyLoading(true);
    await onAddSurveytoCohort(
      cohort.id,
      selectedSurveys.map((s) => String(s.i))
    );
    setSelectedSurveys([]);
    setAddSurveyLoading(false);
  };

  const handleRemoveSurvey = async (surveyId: string) => {
    setRemovingId(surveyId);
    await onRemoveSurveyFromCohort(cohort.id, surveyId);
    setRemovingId(null);
  };

  const handleAssignTeam = async () => {
    if (!selectedTeam) return;
    setAssignTeamLoading(true);
    await onAssignTeam(selectedTeam.id);
    setSelectedTeam(null);
    setAssignTeamLoading(false);
  };

  return (
    <Box sx={{ height: '100%' }}>
      {/* Header */}
      <Box sx={{ display: 'flex', alignItems: 'center', mb: 1, gap: 1 }}>
        {onBack && (
          <IconButton onClick={onBack} size="small">
            <ArrowBackIcon />
          </IconButton>
        )}
        <Box sx={{ flex: 1, minWidth: 0, display: 'flex', alignItems: 'baseline', gap: 2 }}>
          <Typography variant="h6" noWrap>
            {cohort.name}
          </Typography>
          {cohort.systemLeader && (
            <Typography variant="body2" color="text.secondary" noWrap>
              {cohort.systemLeader}
            </Typography>
          )}
        </Box>
        <IconButton size="small" onClick={() => setEmailDialogOpen(true)}>
          <EmailIcon fontSize="small" />
        </IconButton>
        <IconButton size="small" onClick={openEditDialog}>
          <EditIcon fontSize="small" />
        </IconButton>
        <IconButton size="small" onClick={() => setDeleteDialogOpen(true)}>
          <DeleteIcon fontSize="small" />
        </IconButton>
      </Box>

      <Divider sx={{ mb: 2 }} />

      {/* Teams */}
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
        <Autocomplete
          size="small"
          options={uncohortedTeams}
          getOptionLabel={(t) => t.name}
          value={selectedTeam}
          onChange={(_, v) => setSelectedTeam(v)}
          renderInput={(params) => (
            <TextField {...params} placeholder={t('cohorts.add_team')} variant="standard" />
          )}
          sx={{ flex: 1 }}
          isOptionEqualToValue={(a, b) => a.id === b.id}
        />
        <Button
          size="small"
          variant="outlined"
          disabled={!selectedTeam || assignTeamLoading}
          onClick={handleAssignTeam}
        >
          {t('cohorts.add_team')}
        </Button>
      </Box>
      {teams.length === 0 ? (
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          {t('cohorts.no_teams')}
        </Typography>
      ) : (
        <Box sx={{ mb: 2 }}>
          {teams.map((team) => (
            <CohortTeamSection
              key={team.id}
              team={team}
              allSurveys={allSurveys}
              assignedSurveyIds={assignedSurveyIds}
              onEditTeam={onEditTeam}
              onDeleteTeam={onDeleteTeam}
              onAddSurveyToTeam={onAddSurveyToTeam}
              onRemoveSurveyFromTeam={onRemoveSurveyFromTeam}
              onRemoveFromCohort={() => onRemoveTeamFromCohort(team.id)}
              analyzeSurvey={analyzeSurvey}
              loading={loading}
              getParticipants={getParticipants}
              participantsBySurvey={participantsBySurvey}
              setQrModalOpen={setQrModalOpen}
              loadingParticipants={loadingParticipants}
              isMobile={isMobile}
              editLeaderReportDate={editLeaderReportDate}
              getLeaderAndReportDate={getLeaderAndReportDate}
              loadingLeader={loadingLeader}
              leaderBySurvey={leaderBySurvey}
            />
          ))}
        </Box>
      )}

      <Divider sx={{ mb: 2 }} />

      {/* Cohort-level surveys */}
      <Typography variant="subtitle2" gutterBottom>
        {t('cohorts.surveys')}
      </Typography>
      {cohortSurveys.length === 0 ? (
        <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
          {t('cohorts.no_surveys')}
        </Typography>
      ) : (
        <Table size="small" sx={{ mb: 1 }}>
          <TableBody>
            {cohortSurveys.map((s) => (
              <TableSurveysRow
                key={s.i}
                row={s}
                analyzeSurvey={analyzeSurvey}
                loading={loading}
                getParticipants={getParticipants}
                participantsBySurvey={participantsBySurvey}
                setQrModalOpen={setQrModalOpen}
                loadingParticipants={loadingParticipants}
                selectedSurveys={[]}
                handleSelect={() => {}}
                isMobile={isMobile}
                editLeaderReportDate={editLeaderReportDate}
                getLeaderAndReportDate={getLeaderAndReportDate}
                loadingLeader={loadingLeader}
                leaderBySurvey={leaderBySurvey}
                onRemove={
                  removingId === String(s.i) ? undefined : () => handleRemoveSurvey(String(s.i))
                }
              />
            ))}
          </TableBody>
        </Table>
      )}

      {/* Edit dialog */}
      <Dialog open={editDialogOpen} onClose={() => setEditDialogOpen(false)}>
        <DialogTitle>{t('cohorts.edit')}</DialogTitle>
        <DialogContent>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 1, minWidth: 320 }}>
            <Input
              value={editName}
              placeholder={t('cohorts.name')}
              onChange={(e) => setEditName(e.target.value)}
            />
            <Input
              value={editSystemLeader}
              placeholder={t('cohorts.system_leader')}
              onChange={(e) => setEditSystemLeader(e.target.value)}
            />
            <Typography variant="caption" color="text.secondary">
              {t('cohorts.dates_section')}
            </Typography>
            <LocalizationProvider dateAdapter={AdapterDayjs} adapterLocale="en-gb">
              {Array.from({ length: COHORT_DATE_COUNT }, (_, i) => (
                <Box key={i} sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
                  <TextField
                    size="small"
                    label={t('cohorts.date_name_label')}
                    value={editDateNames[i] ?? ''}
                    onChange={(e) => {
                      const next = [...editDateNames];
                      next[i] = e.target.value;
                      setEditDateNames(next);
                    }}
                    sx={{ flex: 1 }}
                  />
                  <DatePicker
                    label={t('cohorts.date_n', { n: String(i + 1) })}
                    value={editDates[i] ?? null}
                    onChange={(val) => {
                      const next = [...editDates];
                      next[i] = val;
                      setEditDates(next);
                    }}
                    slotProps={{ textField: { size: 'small' } }}
                  />
                </Box>
              ))}
            </LocalizationProvider>
            <Divider />
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Autocomplete
                multiple
                size="small"
                options={availableSurveys}
                getOptionLabel={(s) => `${s.t} (${s.i})`}
                value={selectedSurveys}
                onChange={(_, v) => setSelectedSurveys(v)}
                renderInput={(params) => (
                  <TextField {...params} placeholder={t('cohorts.add_survey')} variant="standard" />
                )}
                sx={{ flex: 1 }}
                isOptionEqualToValue={(a, b) => a.i === b.i}
              />
              <Button
                size="small"
                variant="outlined"
                disabled={selectedSurveys.length === 0 || addSurveyLoading}
                onClick={handleAddSurvey}
              >
                {t('cohorts.add_survey')}
              </Button>
            </Box>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setEditDialogOpen(false)}>{t('common.cancel')}</Button>
          <Button onClick={handleEdit} disabled={editLoading || !editName}>
            {editLoading ? <CircularProgress size={20} /> : t('common.confirm')}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Email config dialog */}
      <CohortEmailDialog
        cohort={cohort}
        open={emailDialogOpen}
        onClose={() => setEmailDialogOpen(false)}
        onSave={onEditEmailConfig}
        onUploadAttachment={onUploadAttachment}
        teams={teams}
        allSurveys={allSurveys}
        onSendEmails={onSendEmails}
      />

      {/* Delete dialog */}
      <Dialog open={deleteDialogOpen} onClose={() => setDeleteDialogOpen(false)}>
        <DialogTitle>{t('cohorts.delete_confirm_title')}</DialogTitle>
        <DialogActions>
          <Button onClick={() => setDeleteDialogOpen(false)} disabled={deleteLoading}>
            {t('common.cancel')}
          </Button>
          <Button onClick={handleDelete} color="error" disabled={deleteLoading}>
            {deleteLoading ? <CircularProgress size={20} /> : t('common.confirm')}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};
